import { Modal, Form, Input, DatePicker, Switch, InputNumber, Select, App, Typography } from 'antd'
import { useState, useEffect } from 'react'
import dayjs, { type Dayjs } from 'dayjs'
import { updateAnnouncement } from '../services/announcement-api'
import { useTenants } from '../../tenants/hooks/useTenants'
import { bannerTypeLabels, type Announcement, type BannerType } from '../types/announcement.types'
import { MarkdownPreview } from './MarkdownPreview'

interface EditAnnouncementModalProps {
  open: boolean
  announcement: Announcement | null
  onClose: () => void
  onSuccess: () => void
}

interface FormValues {
  code: string
  message: string
  bannerType: BannerType
  scope: 'global' | 'tenant'
  tenantCode: string | null
  startDate: Dayjs
  endDate: Dayjs
  isActive: boolean
  displayOrder: number
}

const bannerTypeOptions = Object.entries(bannerTypeLabels).map(([value, label]) => ({
  label,
  value
}))

export function EditAnnouncementModal({ open, announcement, onClose, onSuccess }: EditAnnouncementModalProps) {
  const { message } = App.useApp()
  const [form] = Form.useForm<FormValues>()
  const [loading, setLoading] = useState(false)
  const { tenants } = useTenants({ includeInactive: true })
  const scope = Form.useWatch('scope', form)
  const messageValue = Form.useWatch('message', form)

  useEffect(() => {
    if (open && announcement) {
      form.setFieldsValue({
        code: announcement.code,
        message: announcement.message,
        bannerType: announcement.bannerType || 'info',
        scope: announcement.tenantCode ? 'tenant' : 'global',
        tenantCode: announcement.tenantCode,
        startDate: dayjs(announcement.startDate),
        endDate: dayjs(announcement.endDate),
        isActive: announcement.isActive,
        displayOrder: announcement.displayOrder
      })
    }
  }, [open, announcement, form])

  const handleSubmit = async (values: FormValues) => {
    if (!announcement) return

    try {
      setLoading(true)
      await updateAnnouncement(announcement.id, {
        code: values.code,
        message: values.message,
        bannerType: values.bannerType,
        tenantCode: values.scope === 'global' ? null : values.tenantCode,
        startDate: values.startDate.toISOString(),
        endDate: values.endDate.toISOString(),
        isActive: values.isActive,
        displayOrder: values.displayOrder
      })
      message.success('Announcement updated successfully')
      onSuccess()
      onClose()
    } catch (error: any) {
      console.error('Error updating announcement:', error)
      message.error(error.response?.data?.message || 'Failed to update announcement')
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    form.resetFields()
    onClose()
  }

  const tenantOptions = tenants.map(t => ({
    label: `${t.tenantName} (${t.tenantCode})`,
    value: t.tenantCode
  }))

  return (
    <Modal
      title="Edit announcement"
      open={open}
      onOk={() => form.submit()}
      onCancel={handleCancel}
      okText="Save"
      confirmLoading={loading}
      width={600}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          label="Code"
          name="code"
          rules={[
            { required: true, message: 'Please enter code' },
            { max: 50, message: 'Code must not exceed 50 characters' }
          ]}
        >
          <Input placeholder="e.g., maintenance-2024" />
        </Form.Item>

        <Form.Item
          label={
            <span>
              Message{' '}
              <Typography.Text type="secondary" style={{ fontWeight: 'normal', fontSize: '12px' }}>
                (supports markdown: **bold**, *italic*, [link](url))
              </Typography.Text>
            </span>
          }
          name="message"
          rules={[
            { required: true, message: 'Please enter message' },
            { max: 1000, message: 'Message must not exceed 1000 characters' }
          ]}
        >
          <Input.TextArea rows={3} placeholder="Enter announcement message" />
        </Form.Item>

        <div
          style={{
            marginBottom: 16,
            padding: '8px 12px',
            background: '#fafafa',
            borderRadius: 6,
            border: '1px solid #d9d9d9',
            minHeight: 40
          }}
        >
          <Typography.Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: 4 }}>
            Preview:
          </Typography.Text>
          <MarkdownPreview content={messageValue || ''} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <Form.Item label="Banner type" name="bannerType" rules={[{ required: true }]}>
            <Select options={bannerTypeOptions} />
          </Form.Item>

          <Form.Item label="Scope" name="scope" rules={[{ required: true }]}>
            <Select
              options={[
                { label: 'Global (all tenants)', value: 'global' },
                { label: 'Specific tenant', value: 'tenant' }
              ]}
            />
          </Form.Item>
        </div>

        {scope === 'tenant' && (
          <Form.Item
            label="Tenant"
            name="tenantCode"
            rules={[{ required: scope === 'tenant', message: 'Please select a tenant' }]}
          >
            <Select
              showSearch
              placeholder="Select tenant"
              options={tenantOptions}
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            />
          </Form.Item>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <Form.Item
            label="Start date"
            name="startDate"
            rules={[{ required: true, message: 'Please select start date' }]}
          >
            <DatePicker style={{ width: '100%' }} showTime format="YYYY-MM-DD HH:mm" />
          </Form.Item>

          <Form.Item
            label="End date"
            name="endDate"
            rules={[
              { required: true, message: 'Please select end date' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || !getFieldValue('startDate')) {
                    return Promise.resolve()
                  }
                  if (value.isAfter(getFieldValue('startDate'))) {
                    return Promise.resolve()
                  }
                  return Promise.reject(new Error('End date must be after start date'))
                }
              })
            ]}
          >
            <DatePicker style={{ width: '100%' }} showTime format="YYYY-MM-DD HH:mm" />
          </Form.Item>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <Form.Item label="Display order" name="displayOrder">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item label="Active" name="isActive" valuePropName="checked">
            <Switch />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  )
}
